import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AudinexaPipelineDrawer from '../repair/AudinexaPipelineDrawer';
import Pagination, { DEFAULT_PAGE_SIZE, usePaginationSlice } from '../../components/Pagination';
import { ServiceTicketActions } from './ServiceTicketPhase14Actions';
import ServiceSlipPrintModal from './ServiceSlipPrintModal';

import useEscapeKey from '../../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const STATUS_STYLE = {
  // Legacy
  open:        'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved:    'bg-emerald-100 text-emerald-800',
  closed:      'bg-slate-200 text-slate-600',
  cancelled:   'bg-rose-100 text-rose-800',
  // AUDINEXA
  RECEIVED:             'bg-slate-100 text-slate-800',
  INSPECTED:            'bg-blue-100 text-blue-800',
  AWAITING_DISPATCH:    'bg-amber-100 text-amber-800',
  DISPATCHED:           'bg-orange-100 text-orange-800',
  IN_TRANSIT:           'bg-orange-100 text-orange-800',
  DELIVERED_TO_COMPANY: 'bg-indigo-100 text-indigo-800',
  ESTIMATE_PENDING:     'bg-amber-200 text-amber-900',
  CLIENT_APPROVED:      'bg-emerald-100 text-emerald-800',
  CLIENT_REJECTED:      'bg-rose-100 text-rose-800',
  REPAIR_IN_PROGRESS:   'bg-indigo-100 text-indigo-800',
  RETURN_SHIPPED:       'bg-orange-100 text-orange-800',
  READY_FOR_PICKUP:     'bg-emerald-100 text-emerald-800',
  DELIVERED_TO_CLIENT:  'bg-emerald-200 text-emerald-900 font-bold',
  CLOSED:               'bg-slate-200 text-slate-600',
  CANCELLED:            'bg-rose-100 text-rose-800',
};

const KIND_LABEL = {
  repair: 'Repair',
  cleaning: 'Cleaning',
  reprogramming: 'Reprogramming',
  warranty_claim: 'Warranty Claim',
  other: 'Other',
};


export default function ServiceTicketsPage() {
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  // After a successful ticket create, we show a tiny "Print Service
  // Acknowledgement?" popup so the front-desk can hand the patient a
  // claim slip without diving into the ticket drawer. Skipping keeps
  // the existing flow (row selected + list refreshed) unchanged.
  const [ackTicket, setAckTicket] = useState(null);
  const [openNo, setOpenNo] = useState(null);
  const [me, setMe] = useState(null);
  const [page, setPage] = useState(1);
  // Client-side filters (search across ticket_no/patient/mobile/complaint/serial; kind + technician dropdowns)
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [techFilter, setTechFilter] = useState('');

  // SRV-001 · Sprint-3B — accept `?ticket=<ticket_no>` deep-link.
  // The Patient Profile → Service tab passes the ticket number here so
  // the receptionist lands directly on the ticket's pipeline drawer
  // instead of scrolling through the full clinic-wide ticket list.
  // The search box is also prefilled so the row is visible when the
  // drawer is closed. Param is stripped after first-mount to keep the
  // URL clean and prevent re-opening the drawer on browser refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const q = searchParams.get('ticket');
    if (!q) return;
    setOpenNo(q);
    setSearch(q);
    const next = new URLSearchParams(searchParams);
    next.delete('ticket');
    setSearchParams(next, { replace: true });
  }, []);

  // Distinct technician + kind options derived from the currently-loaded rows
  const techOptions = useMemo(() => {
    const m = new Map();
    rows.forEach(r => { if (r.technician_user_id && r.technician_name) m.set(r.technician_user_id, r.technician_name); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = (search.trim() || '').toLowerCase();
    return rows.filter(t => {
      if (kindFilter && t.kind !== kindFilter) return false;
      if (techFilter && t.technician_user_id !== techFilter) return false;
      if (!q) return true;
      const hay = ([
        t.ticket_no, t.patient_name, t.patient_mobile, t.patient_id,
        t.complaint, t.serial_no, t.diagnosis, t.resolution_notes,
        t.technician_name,
      ].filter(Boolean).join(' ') || '').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, kindFilter, techFilter]);

  const pagedRows = usePaginationSlice(filteredRows, page, DEFAULT_PAGE_SIZE);
  useEffect(() => { setPage(1); }, [status, search, kindFilter, techFilter, rows.length]);

  useEffect(() => { (async () => {
    try { setMe((await axios.get(`${API}/auth/me`)).data?.user || null); } catch {/*noop*/}
  })(); }, []);

  const canCreate = useMemo(() => !!me && ['front_desk','audiologist','technician','clinic_owner','super_admin','founder'].includes(me.role), [me]);
  const canMutate = useMemo(() => !!me && ['technician','audiologist','clinic_owner','super_admin'].includes(me.role), [me]);

  const load = useCallback(async () => {
    const params = status ? { status } : {};
    const [r, k] = await Promise.all([
      axios.get(`${API}/ha/service-tickets`, { params }),
      axios.get(`${API}/ha/service-tickets-kpis`),
    ]);
    // Defensive: /ha/service-tickets is offline-cacheable and can also
    // route through a Cloudflare 502 HTML page on transient upstream
    // outages. Both cases surface a non-array body via axios, which
    // then blows up `rows.forEach` in `techOptions` / `rows.filter` in
    // `filteredRows` with "e.forEach is not a function". Guarding at
    // the setter is the smallest possible surface fix — the UI simply
    // shows "No tickets yet." until the next successful poll.
    setRows(Array.isArray(r.data) ? r.data : []);
    setKpis(k.data && typeof k.data === 'object' && !Array.isArray(k.data) ? k.data : null);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setSearch(''); setKindFilter(''); setTechFilter(''); setStatus(''); };
  const hasActiveFilters = !!(search || kindFilter || techFilter || status);

  return (
    <div className="p-5" data-testid="ha-tix-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Service Tickets</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">Repairs, cleaning, reprogramming, warranty claims — full ticket lifecycle with serial state tracking.</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && <button onClick={() => setCreating(true)} data-testid="ha-tix-new" className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md shadow-sm">+ New Ticket</button>}
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-5 gap-3 mb-4">
          <Kpi label="Open"        value={kpis.open}              color="bg-amber-50 text-amber-800 border-amber-200"     testid="ha-tix-kpi-open" />
          <Kpi label="In Progress" value={kpis.in_progress}       color="bg-blue-50 text-blue-800 border-blue-200"         testid="ha-tix-kpi-progress" />
          <Kpi label="Resolved"    value={kpis.resolved}          color="bg-emerald-50 text-emerald-800 border-emerald-200" testid="ha-tix-kpi-resolved" />
          <Kpi label="Closed"      value={kpis.closed}            color="bg-slate-50 text-slate-700 border-slate-200"     testid="ha-tix-kpi-closed" />
          <Kpi label="Warranty"    value={kpis.warranty_covered}  color="bg-indigo-50 text-indigo-800 border-indigo-200"   testid="ha-tix-kpi-warranty" />
        </div>
      )}

      {/* Search & Filter Bar — minimal, sticky-feeling toolbar */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="ha-tix-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">⌕</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticket #, patient, mobile, serial, complaint…"
            data-testid="ha-tix-search"
            className="w-full pl-7 pr-7 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          {search && (
            <button onClick={() => setSearch('')} data-testid="ha-tix-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs">✕</button>
          )}
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ha-tix-status-filter" className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm">
          <option value="">All statuses</option>
          {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="ha-tix-kind-filter" className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm">
          <option value="">All kinds</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} data-testid="ha-tix-tech-filter" className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm">
          <option value="">All technicians</option>
          {techOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {hasActiveFilters && (
          <button onClick={clearFilters} data-testid="ha-tix-clear-filters" className="text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold px-2 py-1.5">Clear filters</button>
        )}
        <span className="text-[11px] text-slate-500 ml-auto" data-testid="ha-tix-result-count">
          Showing <b className="text-slate-800">{filteredRows.length}</b>{filteredRows.length !== rows.length && <> of <b className="text-slate-800">{rows.length}</b></>}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Ticket No</th>
              <th className="px-3 py-2 text-left">Patient</th>
              <th className="px-3 py-2 text-left">Serial</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Complaint</th>
              <th className="px-3 py-2 text-left">Technician</th>
              <th className="px-3 py-2 text-right">Cost</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={9} className="py-10 text-center text-slate-400 italic text-xs" data-testid="ha-tix-empty">
                  {rows.length === 0 ? 'No tickets yet.' : 'No tickets match your filters.'}
                </td>
              </tr>
            )}
            {pagedRows.map(t => (
              <tr key={t.ticket_no} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`ha-tix-row-${t.ticket_no}`}>
                <td className="px-3 py-2 font-mono text-[11px] font-bold text-indigo-700">{t.ticket_no}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-semibold">{t.patient_name || t.patient_id}</div>
                  {t.patient_mobile && <div className="text-[10px] text-slate-500">{t.patient_mobile}</div>}
                </td>
                <td className="px-3 py-2 text-[11px] font-mono">{t.serial_no || '—'}</td>
                <td className="px-3 py-2 text-[10px]">{KIND_LABEL[t.kind] || t.kind} {t.warranty_covered && <span className="ml-1 bg-indigo-100 text-indigo-800 px-1 rounded text-[9px]">W</span>}</td>
                <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={t.complaint}>{t.complaint}</td>
                <td className="px-3 py-2 text-[11px]">{t.technician_name || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs">{t.cost_to_patient ? fmtINR(t.cost_to_patient) : '—'}</td>
                <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS_STYLE[t.status]}`}>{(t.status || '').toUpperCase()}</span></td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setOpenNo(t.ticket_no)} data-testid={`ha-tix-open-${t.ticket_no}`} className="text-[10px] text-indigo-600 font-semibold hover:underline">Open →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} setPage={setPage} total={filteredRows.length} testidPrefix="ha-tix-pagination" />
      </div>

      {creating && <NewTicketModal onClose={() => setCreating(false)} onCreated={(t) => { setCreating(false); load(); setAckTicket(t); }} />}
      {ackTicket && (
        <PrintAckModal
          ticket={ackTicket}
          onClose={() => { const t = ackTicket; setAckTicket(null); setOpenNo(t.ticket_no); }}
        />
      )}
      {openNo && <AudinexaPipelineDrawer ticketNo={openNo} onClose={() => setOpenNo(null)} onChanged={load} />}
    </div>
  );
}


const Kpi = ({ label, value, color, testid }) => (
  <div data-testid={testid} className={`border rounded-md px-3 py-2 ${color}`}>
    <div className="text-[9px] font-semibold uppercase tracking-wider">{label}</div>
    <div className="text-lg font-bold tabular-nums">{value ?? 0}</div>
  </div>
);


// ============ PRINT ACKNOWLEDGEMENT MODAL ============
// Small hand-off dialog rendered right after a successful ticket
// create. "Print" opens the A5 Pre-Service Acknowledgement slip
// (React-rendered, browser-print). Skipping just closes the popup
// and drops the caller into the existing ticket-drawer flow, so the
// rest of the pipeline (loaner issue, courier, etc.) stays untouched.
function PrintAckModal({ ticket, onClose }) {
  useEscapeKey(onClose);
  const [showSlip, setShowSlip] = useState(false);

  if (showSlip) {
    return (
      <ServiceSlipPrintModal
        ticket={ticket}
        pipeline={null}
        mode="dropoff"
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="ha-tix-ack-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-1 text-slate-900">Ticket Created</h2>
        <p className="text-xs text-slate-500 mb-4">
          Ticket <span className="font-mono font-bold text-slate-800">{ticket.ticket_no}</span> is now open.
          Print the drop-off slip (A5) for the patient?
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] mb-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">On the drop-off slip</div>
          <ul className="list-disc list-inside space-y-0.5 text-slate-700">
            <li>Patient · Mobile · MRD</li>
            <li>Hearing aid: Make / Model / Type / Side / Serial / Purchase date</li>
            <li>Accessories received · Warranty card Y/N · Rough estimate</li>
            <li>Complaint · Terms &amp; conditions · Signature blocks</li>
          </ul>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="ha-tix-ack-skip"
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={() => setShowSlip(true)}
            data-testid="ha-tix-ack-print"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-slate-800 hover:bg-slate-900 text-white rounded shadow-sm"
          >
            🖨️ Print Drop-off Slip (A5)
          </button>
        </div>
      </div>
    </div>
  );
}



function NewTicketModal({ onClose, onCreated }) {
  useEscapeKey(onClose);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState('');
  const [patient, setPatient] = useState('');
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [serials, setSerials] = useState([]);
  const [serialFallback, setSerialFallback] = useState(false);
  const [serialId, setSerialId] = useState('');
  const [kind, setKind] = useState('repair');
  const [repairLocation, setRepairLocation] = useState('IN_CLINIC');
  const [complaint, setComplaint] = useState('');
  const [warranty, setWarranty] = useState(false);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Instrument details (data-capture only) ──────────────────────
  // Type + side are the only two the user must fill; the rest are
  // free-text captures that get persisted verbatim.
  const [insMake, setInsMake] = useState('');
  const [insModel, setInsModel] = useState('');
  const [insType, setInsType] = useState('');
  const [insSerial, setInsSerial] = useState('');
  // Feb 2026 · replaced legacy "Single/Both" count with hearing-aid Side
  // (Left / Right / Both) so the printed slip reads correctly.
  const [haSide, setHaSide] = useState('');                // 'left' | 'right' | 'both'
  const [purchasedFrom, setPurchasedFrom] = useState('');  // 'our_clinic' | 'outside'
  const [purchasedFromName, setPurchasedFromName] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [warrantyCardFile, setWarrantyCardFile] = useState(null);
  const [instrumentImages, setInstrumentImages] = useState([]);   // up to 4 (INTERNAL ref only)

  // Feb 2026 · Drop-off Slip additions
  const [accessoriesGiven, setAccessoriesGiven] = useState([]);   // array of keys
  const [customAccessory, setCustomAccessory] = useState('');
  const [warrantyCardGiven, setWarrantyCardGiven] = useState(false);
  const [roughEstimate, setRoughEstimate] = useState('');
  // Sep 2026 · Warranty Status radio. Drives both the "warranty covered"
  // flag on the ticket AND the on-slip notice. Values: 'under_warranty' |
  // 'out_of_warranty'. Required at drop-off. When set to under_warranty
  // we auto-tick the warranty_covered checkbox and disable it so downstream
  // billing zero-rates the service line without any second guess.
  const [warrantyStatus, setWarrantyStatus] = useState('');

  const toggleAccessory = (key) => {
    setAccessoriesGiven(prev => prev.includes(key)
      ? prev.filter(k => k !== key)
      : [...prev, key]);
  };

  useEffect(() => { (async () => {
    const b = await axios.get(`${API}/branches`);
    setBranches(b.data);
    if (b.data[0]) setBranch(b.data[0].branch_id);
  })(); }, []);

  useEffect(() => {
    if (!search || search.length < 2) { setPatients([]); return; }
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/patients`, { params: { search, limit: 10 } });
        if (!cancelled) setPatients(Array.isArray(r.data) ? r.data : []);
      } catch { if (!cancelled) setPatients([]); }
    }, 200);
    return () => { cancelled = true; clearTimeout(h); };
  }, [search]);

  // When patient picked, fetch their serials. Primary lookup by
  // current_patient_id. If empty (legacy sale predating the patient-stamp
  // fix), fall back to listing the clinic's SOLD units so the front desk can
  // still pick the right one manually instead of being stuck.
  useEffect(() => {
    if (!patient) { setSerials([]); setSerialFallback(false); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/ha/serial-items`, { params: { current_patient_id: patient, limit: 50 } });
        const owned = Array.isArray(r.data) ? r.data : [];
        if (owned.length > 0) {
          setSerials(owned);
          setSerialFallback(false);
          return;
        }
        // Fallback — show clinic's SOLD/AT_SERVICE units (manual pick).
        const f = await axios.get(`${API}/ha/serial-items`, { params: { state: 'SOLD', limit: 100 } });
        setSerials(Array.isArray(f.data) ? f.data : []);
        setSerialFallback(true);
      } catch { setSerials([]); setSerialFallback(false); }
    })();
  }, [patient]);

  const submit = async () => {
    setErr('');
    if (!patient) { setErr('Pick a patient'); return; }
    if (!insType) { setErr('Instrument type is required'); return; }
    if (!haSide) { setErr('Hearing-aid side is required'); return; }
    if (!warrantyStatus) { setErr('Warranty status is required'); return; }
    if (purchasedFrom === 'outside' && !purchasedFromName.trim()) {
      setErr('Clinic / store name is required when purchased outside'); return;
    }
    if (accessoriesGiven.includes('custom') && !customAccessory.trim()) {
      setErr('Please name the custom accessory'); return;
    }
    if (!complaint || complaint.length < 5) { setErr('Complaint must be ≥ 5 chars'); return; }
    setSaving(true);
    try {
      const body = {
        branch_id: branch, patient_id: patient, kind, complaint,
        warranty_covered: (warrantyStatus === 'under_warranty') ? true : false,
        repair_location: repairLocation,
        instrument_make: insMake || null,
        instrument_model: insModel || null,
        instrument_type: insType,
        instrument_serial: insSerial || null,
        hearing_aid_side: haSide,
        warranty_status: warrantyStatus,
        purchased_from: purchasedFrom || null,
        purchased_from_name: purchasedFrom === 'outside' ? purchasedFromName.trim() : null,
        purchase_date: purchaseDate || null,
        // Feb 2026 · Drop-off Slip additions
        accessories_given: accessoriesGiven,
        custom_accessory: accessoriesGiven.includes('custom') ? customAccessory.trim() : null,
        warranty_card_given: warrantyCardGiven,
        rough_estimate: roughEstimate.trim() || null,
      };
      if (serialId) body.serial_id = serialId;
      const r = await axios.post(`${API}/ha/service-tickets`, body);
      const ticketNo = r.data.ticket_no;

      // Upload attachments AFTER the ticket exists. Failures here are
      // logged inline (setErr) but the ticket itself still counts as
      // created — the front-desk can retry uploads from the ticket
      // detail drawer without recreating the record.
      const uploadOne = async (fileObj, uKind) => {
        const fd = new FormData();
        fd.append('kind', uKind);
        fd.append('file', fileObj);
        return axios.post(
          `${API}/ha/service-tickets/${ticketNo}/attachments`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
      };
      try {
        if (warrantyCardFile) await uploadOne(warrantyCardFile, 'warranty_card');
        for (const img of instrumentImages) await uploadOne(img, 'image');
      } catch (uploadErr) {
        setErr(
          `Ticket ${ticketNo} was created but one or more attachments failed: ` +
          (uploadErr?.response?.data?.detail || 'Unknown upload error') +
          ' — reopen the ticket to retry.',
        );
      }

      // Pass the just-created ticket fields to the print modal so the
      // A5 slip renders WITHOUT needing another network round-trip.
      onCreated({ ...r.data, ...body });
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ha-tix-modal">
        <h2 className="text-lg font-bold mb-3">New Service Ticket</h2>
        {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-3">{err}</div>}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Patient *</span>
            {patient ? (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-sm">
                <span className="flex-1">{patients.find(p => p.patient_id === patient)?.name || patient}</span>
                <button onClick={() => { setPatient(''); setSearch(''); setSerialId(''); }} className="text-rose-500 text-xs">✕</button>
              </div>
            ) : (
              <>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / mobile / MRD…" data-testid="ha-tix-patient-search" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
                {patients.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-auto border border-slate-200 rounded">
                    {patients.map(p => (
                      <button key={p.patient_id} onClick={() => setPatient(p.patient_id)} className="block w-full text-left text-xs px-2 py-1 hover:bg-indigo-50" data-testid={`ha-tix-patient-pick-${p.patient_id}`}>
                        <span className="font-semibold">{p.name}</span> <span className="text-slate-500">({p.mobile || '—'})</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Branch</span>
            <select value={branch} onChange={(e) => setBranch(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="ha-tix-branch">
              {branches.map(b => <option key={b.branch_id} value={b.branch_id}>{b.name}</option>)}
            </select>
          </div>
        </div>

        {patient && (
          <div className="mb-3">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Unit being serviced</span>
            <select value={serialId} onChange={(e) => setSerialId(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="ha-tix-serial">
              <option value="">— (no specific unit / lost unit)</option>
              {serials.map(s => <option key={s.serial_id} value={s.serial_id}>{`${s.serial_no} · ${s.state}`}</option>)}
            </select>
            {serials.length === 0 && (
              <div className="text-[10px] italic text-amber-700 mt-0.5">
                No hearing aids on file for this patient yet. Pick "(no specific unit)" if it's a lost / legacy device — or record the sale in HA Sales first.
              </div>
            )}
            {serialFallback && serials.length > 0 && (
              <div className="text-[10px] italic text-amber-700 mt-0.5">
                No unit auto-linked to this patient — showing all <b>SOLD</b> units in the clinic. Pick the right one manually (legacy sale).
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="ha-tix-kind">
              {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="text-xs inline-flex items-center gap-2 cursor-pointer" title="Auto-set from Warranty Status below">
              <input type="checkbox"
                     checked={warrantyStatus === 'under_warranty'}
                     onChange={() => {}}
                     disabled
                     data-testid="ha-tix-warranty" />
              <span className="text-slate-500">Warranty covered (auto)</span>
            </label>
          </div>
        </div>

        {/* Sep 2026 · Warranty Status — required at drop-off. Drives the
            warranty_covered flag on the ticket AND prints on both A5 slips.
            When "under_warranty" is picked, we also switch the billing flow
            to zero-service-charge; courier fees are still chargeable at
            invoice-raise time as a separate line item. */}
        <div className="mb-3" data-testid="ha-tix-warranty-status-group">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Warranty Status *</span>
          <div className="flex gap-2">
            <label className={`flex-1 cursor-pointer border rounded-md px-3 py-2 text-xs ${warrantyStatus === 'under_warranty' ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
              <input
                type="radio" className="mr-1.5" name="warranty_status" value="under_warranty"
                checked={warrantyStatus === 'under_warranty'}
                onChange={() => setWarrantyStatus('under_warranty')}
                data-testid="ha-tix-warranty-under"
              />
              <b>Under Warranty</b> — no service charge
            </label>
            <label className={`flex-1 cursor-pointer border rounded-md px-3 py-2 text-xs ${warrantyStatus === 'out_of_warranty' ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
              <input
                type="radio" className="mr-1.5" name="warranty_status" value="out_of_warranty"
                checked={warrantyStatus === 'out_of_warranty'}
                onChange={() => setWarrantyStatus('out_of_warranty')}
                data-testid="ha-tix-warranty-out"
              />
              <b>Out of Warranty</b> — chargeable
            </label>
          </div>
          {warrantyStatus === 'under_warranty' && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-900" data-testid="ha-tix-warranty-under-notice">
              <b>Under Warranty selected.</b> Service charge defaults to <b>₹0</b> on the invoice.
              Courier charges (if any) can be added as a separate line item when raising the invoice.
              Please attach the warranty card or purchase invoice below for internal reference.
            </div>
          )}
        </div>

        {/* Repair location — the audiologist's decision at inspect-time.
            IN_CLINIC = we'll rectify here (no courier flow). VENDOR = ship to
            manufacturer (courier + estimate + customer approval flows fire). */}
        <div className="mb-3" data-testid="ha-tix-repair-location-group">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Repair Location *</span>
          <div className="flex gap-2">
            <label className={`flex-1 cursor-pointer border rounded-md px-3 py-2 text-xs ${repairLocation === 'IN_CLINIC' ? 'border-indigo-500 bg-indigo-50 text-indigo-900' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
              <input
                type="radio" className="mr-1.5" name="repair_loc" value="IN_CLINIC"
                checked={repairLocation === 'IN_CLINIC'}
                onChange={() => setRepairLocation('IN_CLINIC')}
                data-testid="ha-tix-repair-location-in-clinic"
              />
              <b>In-clinic</b> — fix here, no courier
            </label>
            <label className={`flex-1 cursor-pointer border rounded-md px-3 py-2 text-xs ${repairLocation === 'VENDOR' ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
              <input
                type="radio" className="mr-1.5" name="repair_loc" value="VENDOR"
                checked={repairLocation === 'VENDOR'}
                onChange={() => setRepairLocation('VENDOR')}
                data-testid="ha-tix-repair-location-vendor"
              />
              <b>Send to vendor</b> — ship to manufacturer
            </label>
          </div>
        </div>

        <div className="mb-3">
          {/* ── Instrument Details (data-capture only) ────────────────
              Rendered ABOVE the Complaint textarea per requirement.
              Type + count are the only two required inputs; the rest
              are free-text or optional file uploads. */}
          <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-3" data-testid="ha-tix-instrument-section">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-bold">Instrument Details</div>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Make</span>
                <input value={insMake} onChange={(e) => setInsMake(e.target.value)} placeholder="Phonak, Signia, Widex, Oticon…" data-testid="ha-tix-ins-make" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Model</span>
                <input value={insModel} onChange={(e) => setInsModel(e.target.value)} placeholder="e.g. Audeo P50-R" data-testid="ha-tix-ins-model" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Type *</span>
                <select value={insType} onChange={(e) => setInsType(e.target.value)} data-testid="ha-tix-ins-type" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
                  <option value="">— select —</option>
                  {['BTE', 'RIC', 'CIC', 'ITC', 'ITE', 'IIC', 'Custom'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Serial Number</span>
                <input value={insSerial} onChange={(e) => setInsSerial(e.target.value)} placeholder="Instrument serial" data-testid="ha-tix-ins-serial" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Hearing-aid Side *</span>
                <div className="flex gap-3 text-xs">
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="ha_side" value="left" checked={haSide === 'left'} onChange={() => setHaSide('left')} data-testid="ha-tix-ha-side-left" />
                    Left
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="ha_side" value="right" checked={haSide === 'right'} onChange={() => setHaSide('right')} data-testid="ha-tix-ha-side-right" />
                    Right
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="ha_side" value="both" checked={haSide === 'both'} onChange={() => setHaSide('both')} data-testid="ha-tix-ha-side-both" />
                    Both
                  </label>
                </div>
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Purchased From</span>
                <div className="flex gap-3 text-xs">
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="purchased_from" value="our_clinic" checked={purchasedFrom === 'our_clinic'} onChange={() => { setPurchasedFrom('our_clinic'); setPurchasedFromName(''); }} data-testid="ha-tix-purchased-ours" />
                    Our Clinic
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="purchased_from" value="outside" checked={purchasedFrom === 'outside'} onChange={() => setPurchasedFrom('outside')} data-testid="ha-tix-purchased-outside" />
                    Outside
                  </label>
                </div>
              </div>
            </div>
            {purchasedFrom === 'outside' && (
              <div className="mb-2" data-testid="ha-tix-purchased-outside-name-wrap">
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Clinic / Store Name *</span>
                <input value={purchasedFromName} onChange={(e) => setPurchasedFromName(e.target.value)} placeholder="Where the patient bought it" data-testid="ha-tix-purchased-outside-name" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Purchase Date</span>
                <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} data-testid="ha-tix-purchase-date" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Warranty Card (upload)</span>
                <input
                  type="file" accept="image/*,application/pdf"
                  onChange={(e) => setWarrantyCardFile(e.target.files?.[0] || null)}
                  data-testid="ha-tix-warranty-card"
                  className="w-full text-xs"
                />
                {warrantyCardFile && (
                  <div className="text-[10px] text-slate-500 mt-0.5 truncate">{warrantyCardFile.name}</div>
                )}
              </div>
            </div>
            {/* Feb 2026 · Warranty-card given toggle (Y/N flag independent
                of the file upload — the front-desk records whether the
                patient handed a physical card, regardless of whether we
                scanned it). */}
            <div className="mb-2">
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="ha-tix-warranty-card-given-wrap">
                <input
                  type="checkbox"
                  checked={warrantyCardGiven}
                  onChange={(e) => setWarrantyCardGiven(e.target.checked)}
                  data-testid="ha-tix-warranty-card-given"
                />
                <span className="text-slate-700 font-semibold">Warranty card given by patient</span>
              </label>
            </div>
            {/* Feb 2026 · Accessories received at drop-off */}
            <div className="mb-2" data-testid="ha-tix-accessories-section">
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Accessories Received</span>
              <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
                {[
                  ['charger', 'Charger'],
                  ['dome', 'Dome'],
                  ['wax_guards', 'Wax-guards'],
                  ['ear_hook', 'Ear-hook'],
                  ['cable', 'Cable'],
                  ['case', 'Case'],
                  ['battery', 'Battery'],
                  ['cleaning_tool', 'Cleaning tool'],
                  ['custom', 'Custom'],
                ].map(([key, label]) => (
                  <label key={key} className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={accessoriesGiven.includes(key)}
                      onChange={() => toggleAccessory(key)}
                      data-testid={`ha-tix-accessory-${key}`}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {accessoriesGiven.includes('custom') && (
                <input
                  value={customAccessory}
                  onChange={(e) => setCustomAccessory(e.target.value)}
                  placeholder="Name the accessory *"
                  data-testid="ha-tix-custom-accessory"
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-xs"
                />
              )}
            </div>
            {/* Feb 2026 · Rough estimate (free-text, optional) */}
            <div className="mb-2">
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Rough Estimate (optional)</span>
              <input
                value={roughEstimate}
                onChange={(e) => setRoughEstimate(e.target.value)}
                placeholder='e.g. "₹3,500 – ₹5,000" or "TBD after inspection"'
                data-testid="ha-tix-rough-estimate"
                className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
              />
              <div className="text-[10px] text-slate-500 mt-0.5 italic">
                Prints on the drop-off slip as an indicative-only figure.
              </div>
            </div>
            <div>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Internal Reference Images (up to 4 · not printed)</span>
              <input
                type="file" accept="image/*" multiple
                onChange={(e) => {
                  const arr = Array.from(e.target.files || []).slice(0, 4);
                  setInstrumentImages(arr);
                }}
                data-testid="ha-tix-instrument-images"
                className="w-full text-xs"
              />
              {instrumentImages.length > 0 && (
                <div className="mt-1 text-[10px] text-slate-500">
                  {instrumentImages.length} image{instrumentImages.length === 1 ? '' : 's'} queued: {instrumentImages.map(f => f.name).join(', ')}
                </div>
              )}
            </div>
          </div>

          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Complaint *</span>
          <textarea value={complaint} onChange={(e) => setComplaint(e.target.value)} rows={3} placeholder="Describe the problem in detail…" data-testid="ha-tix-complaint" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="ha-tix-submit" className="px-4 py-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded">
            {saving ? 'Saving…' : 'Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}

